import React, { useEffect, useRef, useState } from 'react'
import '@whereby.com/browser-sdk/embed'
import styled, { css } from 'styled-components'
import { LiveInterviewType } from 'types'
import { formatDateTimeTimezone } from 'utils/date'
import { Subtitle } from 'components/styled/typography'
import rrLogo from 'components/styled/assets/recright-logo.svg'
import Error404 from 'components/errors/404'
import Error403 from 'components/errors/403'
import { connect } from 'react-redux'
import { clearIntercomCookies } from 'chat-bots/intercom'
import { ThemeSpace } from 'theme/spaces'
import { I18nComponentType } from 'i18n-types'
import { mapLanguageToComponentProps } from 'i18n'
import StructuredInterview from './structured-interview'
import { Button } from 'components/styled/button'
import { Flex } from 'components/styled/containers/flex'
import { ThemeColor } from 'theme/colors'

const LOCALIZED_TIMER_STRINGS: any = {
  en: { d: 'd', h: 'h', m: 'm', s: 's' },
  fi: { d: 'pv', h: 't', m: 'm', s: 's' },
  se: { d: 'd', h: 't', m: 'm', s: 's' },
}

// Add custom JSX intrinsic element type for whereby-embed
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'whereby-embed': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLDivElement>,
        HTMLDivElement
      > & {
        id?: string
        ref?: React.Ref<HTMLDivElement>
        breakout?: string
        locking?: string
        moreButton?: string
        background?: string
        participantCount?: string
        floatSelf?: string
        pipButton?: string
        timer?: string
        topToolbar?: string
        subgridLabels?: string
        toolbarDarkText?: string
        lang?: string
        displayName?: string
        room?: string
      }
    }
  }
}

interface LiveRoomProps {
  liveInterview: LiveInterviewType
  roomUrl?: string
  participant: {
    name?: string
    email: string
    language?: string
    roomCode?: string
    evaluated?: boolean
  }
  participantType?: 'host' | 'guest'
  error: number | null
  i18n: I18nComponentType
  saveNotes: (stepId: string, noteType: string, notes: string) => void
  guideModel: string
  updateLastAccess?: (
    liveInterviewId: string,
    roomCode: string
  ) => Promise<LiveInterviewType>
}

const StructuredInterviewContainer = styled.div<{
  $view?: 'info' | 'default'
  $participantType?: 'host' | 'guest'
  $timeLeft?: boolean
}>`
  width: 100vw;
  height: 100vh;
  padding: ${({ theme }) => theme.space.s16};

  #whereby-embed {
    height: ${(props) =>
      props.$participantType === 'host' ? '77vh' : '100vh'};

    ${({ $timeLeft }) =>
      $timeLeft &&
      css`
        height: 90vh;
      `}
  }

  ${({ $view }) =>
    $view === 'info' &&
    css`
      display: flex;
      justify-content: center;
    `}

  background: ${({ theme, $view }) =>
    $view === 'info' ? theme.color.white : theme.color.black};
`

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 800px;
  text-align: center;
  margin-top: ${({ theme }) => theme.space.s40};
  padding: ${({ theme }) => theme.space.s40};
`

const StyledRRLogo = styled.img`
  max-width: ${({ theme }) => theme.space.s240};
  margin-bottom: ${({ theme }) => theme.space.s32};

  ${({ theme }) => theme.mediaQuery.phone} {
    max-width: ${({ theme }) => theme.space.s128};
    margin-bottom: ${({ theme }) => theme.space.s20};
  }
`

const StyledText = styled(Subtitle)<{
  $size?: ThemeSpace
  $color?: ThemeColor
}>`
  color: ${({ theme, $color }) =>
    $color ? theme.color[$color] : theme.color.bodyText};
  margin-bottom: ${({ theme }) => theme.space.s16};
  line-height: 1.5;
  font-size: ${({ theme, $size }) =>
    $size ? theme.space[$size] : theme.space.s24};
  font-family: ${({ theme }) => theme.font.primary};
`

const mapStateToProps = (state: any) => ({
  error: state.global.httpError,
})

function LiveRoom({
  liveInterview,
  roomUrl,
  participant,
  error,
  i18n,
  participantType = 'guest',
  saveNotes,
  guideModel,
  updateLastAccess,
}: LiveRoomProps) {
  const [timeLeft, setTimeLeft] = useState<string | null>(null)
  const [fiveMinutesLeft, setFiveMinutesLeft] = useState(false)
  const [hostInGuidedInterview, setHostInGuidedInterview] = useState(false)
  const [showCall, setShowCall] = useState(false)
  const [userJoined, setUserJoined] = useState(false)
  const [userLeft, setUserLeft] = useState(false)
  const [interviewEndedByHost, setInterviewEndedByHost] = useState(false)
  const timerRef = useRef<null | ReturnType<typeof setInterval>>(null)
  const wherebyRef = useRef<HTMLDivElement | null>(null)
  const globalWindow: any = window
  const currentPath = window.location.pathname
  const candidate: any = liveInterview.singleCandidate

  // update payload of logged in user to contain more info

  const handleRef = (node: HTMLDivElement | null) => {
    if (node) {
      node.addEventListener('join', (event: any) => {
        setUserJoined(true)
        // update last access and candidate stage here
        if (updateLastAccess && participant.roomCode) {
          updateLastAccess(liveInterview._id, participant.roomCode)
        }
      })

      node.addEventListener('leave', (event: any) => {
        setUserLeft(true)
      })

      node.addEventListener('meeting_end', (event: any) => {
        // TODO: should this set interview finished? + update endTime to match this time? Could be done by accident so not sure
        setInterviewEndedByHost(true)
      })
    }
    wherebyRef.current = node
  }

  // if participant is host and interview guide is not empty
  useEffect(() => {
    if (
      participant &&
      participantType === 'host' &&
      liveInterview.interviewGuide &&
      liveInterview.interviewGuide.steps.length > 0
    ) {
      setHostInGuidedInterview(true)
    }
  }, [participant, participantType, liveInterview.interviewGuide])

  useEffect(() => {
    if (!liveInterview?.startTime) return

    const startTime = new Date(liveInterview.startTime).getTime()

    const updateCountdown = () => {
      const now = new Date().getTime()
      // set started to true if the difference is less than 5 minutes
      const difference = startTime - now
      if (difference <= 5 * 60 * 1000 && !fiveMinutesLeft) {
        setFiveMinutesLeft(true)
      }

      if (difference <= 0) {
        if (timerRef.current) {
          clearInterval(timerRef.current)
        }
        setShowCall(true)
        setTimeLeft(null) // Interview has started
        setFiveMinutesLeft(false)
        return
      }
      const days = Math.floor(difference / (1000 * 60 * 60 * 24))
      const hours = Math.floor((difference / (1000 * 60 * 60)) % 24)
      const minutes = Math.floor((difference / (1000 * 60)) % 60)
      const seconds = Math.floor((difference / 1000) % 60)
      const lang = participant?.language || 'en'

      let timeString = ''
      if (days > 0) {
        timeString += `${days}${LOCALIZED_TIMER_STRINGS[lang].d} `
      }
      timeString += `${hours}${LOCALIZED_TIMER_STRINGS[lang].h} ${minutes}${LOCALIZED_TIMER_STRINGS[lang].m} ${seconds}${LOCALIZED_TIMER_STRINGS[lang].s}`

      setTimeLeft(timeString)
    }

    timerRef.current = setInterval(updateCountdown, 1000)

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }, [liveInterview?.startTime])

  useEffect(() => {
    if (globalWindow && globalWindow.Intercom) {
      globalWindow.Intercom('shutdown')
    }
    clearIntercomCookies(true)
  }, [globalWindow.Intercom])

  if (error === 403) {
    return <Error403 />
  }
  if (error || !liveInterview) {
    return <Error404 />
  }

  const evaluationsButton = () => {
    if (
      liveInterview.interviewGuide &&
      participantType === 'host' &&
      !participant.evaluated
    ) {
      return (
        <Button
          icon="grading"
          variant="primary"
          text={i18n.liveRoom.goToEvaluations}
          onClick={() => {
            /*  if (participant.roomCode) {
            // if this is to work with non-logged in users too at some point, we need this
            window.location.pathname = `${currentPath}/evaluation`
          } */
            window.location.pathname = `${currentPath.replace(
              'enter',
              'evaluation'
            )}`
          }}
        />
      )
    }
    return null
  }

  const callStartingInfo = (inCall?: boolean) => {
    const formattedStartTime = formatDateTimeTimezone(
      liveInterview?.startTime,
      liveInterview?.timezone
    )

    return (
      <InfoContainer>
        <StyledRRLogo src={rrLogo} alt="recright-logo" width={200} />
        {participant && (
          <StyledText $size="s24" $color={inCall ? 'white' : 'bodyText'}>
            {i18n.liveRoom.greeting(participant.name || participant.email)}
          </StyledText>
        )}
        {liveInterview.title && !inCall && (
          <StyledText $color={inCall ? 'white' : 'bodyText'} $size="s20">
            {i18n.liveRoom.interviewTitle(liveInterview.title)}
          </StyledText>
        )}
        <StyledText $color={inCall ? 'white' : 'bodyText'} $size="s20">
          {i18n.liveRoom.notYetStarted(formattedStartTime)}
        </StyledText>
        {timeLeft && (
          <StyledText $size="s24" $color={inCall ? 'white' : 'bodyText'}>
            {i18n.liveRoom.interviewStartsIn(timeLeft)}
          </StyledText>
        )}
        {fiveMinutesLeft && !inCall && (
          <Button
            icon="login"
            variant="primary"
            text={i18n.liveRoom.joinInterview}
            onClick={() => setShowCall(true)}
          />
        )}
      </InfoContainer>
    )
  }

  if (
    !hostInGuidedInterview &&
    !showCall &&
    new Date(liveInterview?.startTime) > new Date()
  ) {
    return (
      <StructuredInterviewContainer $view="info">
        {callStartingInfo()}
      </StructuredInterviewContainer>
    )
  }

  const endTimeHasPassed = liveInterview.endTime
    ? liveInterview.endTime < new Date()
    : false

  if (
    !participant ||
    liveInterview.status === 'finished' ||
    liveInterview.isFinished
  ) {
    return (
      <StructuredInterviewContainer $view="info">
        <InfoContainer>
          <StyledRRLogo src={rrLogo} alt="recright-logo" width={200} />
          <StyledText>{i18n.liveRoom.interviewHasEnded}</StyledText>
          {/* also check when we have data that evaluation is not finished for this user */}
          {evaluationsButton()}
        </InfoContainer>
      </StructuredInterviewContainer>
    )
  }

  if (userLeft || interviewEndedByHost) {
    return (
      <StructuredInterviewContainer $view="info">
        <InfoContainer>
          <StyledRRLogo src={rrLogo} alt="recright-logo" width={200} />
          <StyledText>
            {interviewEndedByHost
              ? i18n.liveRoom.interviewHasEnded
              : i18n.liveRoom.youHaveLeft}
          </StyledText>
          <Flex flexDirection="column" gap="s12" alignItems="center">
            {!endTimeHasPassed && (
              <Button
                icon="login"
                variant="secondary"
                text={i18n.liveRoom.rejoinInterview}
                onClick={() => window.location.reload()}
              />
            )}
            {evaluationsButton()}
          </Flex>
        </InfoContainer>
      </StructuredInterviewContainer>
    )
  }

  const getLang = () => {
    if (participant.language === 'se') {
      return 'sv'
    }
    return participant.language || 'en'
  }

  const lang = getLang()

  const callComponent = (
    <whereby-embed
      // @ts-ignore
      id="whereby-embed"
      ref={handleRef}
      breakout="off"
      locking="off"
      moreButton="off"
      background="off"
      participantCount="off"
      floatSelf="on"
      pipButton="off"
      topToolbar="off"
      subgridLabels="off"
      timer="on"
      lang={lang}
      displayName={participant.name || participant.email}
      room={roomUrl}
    />
  )

  if (roomUrl && (showCall || hostInGuidedInterview) && participant) {
    if (liveInterview.interviewGuide?.steps && participantType === 'host') {
      return (
        <StructuredInterviewContainer $participantType="host">
          <StructuredInterview
            view={userJoined ? 'in-call' : 'pre-call'}
            saveNotes={saveNotes}
            guideModel={guideModel}
            userEmail={participant.email}
            interviewGuideSteps={liveInterview.interviewGuide?.steps}
            candidate={candidate} // TODO if screening candidate, we get more info
            interviewTitle={liveInterview.title}
            timeLeft={timeLeft}
          >
            {fiveMinutesLeft || !timeLeft
              ? callComponent
              : callStartingInfo(true)}
          </StructuredInterview>
        </StructuredInterviewContainer>
      )
    }

    return (
      <StructuredInterviewContainer $timeLeft={!!timeLeft}>
        {/* @ts-ignore */}
        {timeLeft && (
          <Flex justifyContent="center" alignItems="center" pt="s16">
            <StyledText $size="s20" mb="s0" $color="white" fontWeight={400}>
              {i18n.liveRoom.interviewStartsIn(timeLeft)}
            </StyledText>
          </Flex>
        )}
        {callComponent}
      </StructuredInterviewContainer>
    )
  }
  return null
}

export default connect(mapLanguageToComponentProps(mapStateToProps))(LiveRoom)
